import { useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Ban, CheckCircle2, LoaderCircle, Pill, ShieldCheck, X } from "lucide-react";
import type { CrmConsultation, CrmMedication, MedicationInput, MedicationRoute } from "./api";
import { HealthFields } from "./health-record-fields";

// One renderer/composer reused by persistent CRM and both temporary-access surfaces.
type MedicationConsultation = Pick<CrmConsultation, "id" | "occurredAt" | "reason" | "status"> & {
  canAttach?: boolean;
};

const ROUTE_LABEL: Record<MedicationRoute, string> = {
  ORAL: "Oral",
  TOPICAL: "Tópica",
  OPHTHALMIC: "Oftálmica",
  OTIC: "Ótica",
  INJECTABLE: "Inyectable",
  INHALED: "Inhalada",
  OTHER: "Otra",
};

interface MedicationPanelProps {
  patientName: string;
  medications: CrmMedication[];
  consultations: MedicationConsultation[];
  canWrite: boolean;
  onCreated: (input: MedicationInput, idempotencyKey: string) => Promise<CrmMedication>;
  onStopped: (
    medicationId: string,
    input: { status: "COMPLETED" | "DISCONTINUED"; reason: string },
    idempotencyKey: string,
  ) => Promise<CrmMedication>;
}

export function MedicationPanel({
  patientName,
  medications,
  consultations,
  canWrite,
  onCreated,
  onStopped,
}: MedicationPanelProps): ReactNode {
  const [composerOpen, setComposerOpen] = useState(false);
  const [stoppingId, setStoppingId] = useState<string | null>(null);
  const [stopStatus, setStopStatus] = useState<"COMPLETED" | "DISCONTINUED">("COMPLETED");
  const [stopReason, setStopReason] = useState("");
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const stopIdempotencyKeyRef = useRef<string | null>(null);
  const eligibleConsultations = consultations.filter(
    (consultation) => consultation.canAttach && consultation.status !== "VOIDED",
  );
  const active = medications.filter((medication) => medication.status === "ACTIVE");
  const historical = medications.filter((medication) => medication.status !== "ACTIVE");

  async function stopMedication(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!stoppingId) return;
    setStopping(true);
    setError(null);
    const idempotencyKey = stopIdempotencyKeyRef.current ?? crypto.randomUUID();
    stopIdempotencyKeyRef.current = idempotencyKey;
    try {
      await onStopped(
        stoppingId,
        { status: stopStatus, reason: stopReason.trim() },
        idempotencyKey,
      );
      setStoppingId(null);
      setStopReason("");
      setStopStatus("COMPLETED");
      stopIdempotencyKeyRef.current = null;
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "No pudimos finalizar la indicación.");
    } finally {
      setStopping(false);
    }
  }

  return (
    <section className="panel medication-panel" aria-labelledby="medications-title">
      <div className="panel-heading">
        <div>
          <span className="eyebrow">Tratamiento verificable</span>
          <h2 id="medications-title">Tratamientos</h2>
        </div>
        <span className="medication-active-count">
          <Pill size={16} aria-hidden="true" /> {active.length} {active.length === 1 ? "activo" : "activos"}
        </span>
      </div>
      {active.length ? (
        <ul className="medication-list">
          {active.map((medication) => (
            <li key={medication.id}>
              <div className="medication-dose">
                <span aria-hidden="true">
                  <Pill size={17} />
                </span>
                <div>
                  <strong>{medication.name}</strong>
                  <p>
                    {medication.dosage} · {medication.frequency}
                  </p>
                  <small>
                    {ROUTE_LABEL[medication.route]} · desde {formatClinicalDate(medication.startAt)}
                    {medication.endAt ? ` hasta ${formatClinicalDate(medication.endAt)}` : ""}
                  </small>
                </div>
              </div>
              {medication.instructions && (
                <p className="medication-instructions">{medication.instructions}</p>
              )}
              <details>
                <summary>Ver indicación completa</summary>
                <HealthFields record={medication} />
              </details>
              <div className="medication-provenance">
                <ShieldCheck size={13} aria-hidden="true" />
                {medication.prescriberName}
                {medication.prescriberLicense ? ` · Mat. ${medication.prescriberLicense}` : ""}
                {" · "}
                {medication.organizationName}
              </div>
              {medication.canStop &&
                (stoppingId === medication.id ? (
                  <form
                    className="medication-stop-form"
                    onSubmit={(event) => void stopMedication(event)}
                  >
                    <label>
                      <span>Resultado</span>
                      <select
                        value={stopStatus}
                        onChange={(event) =>
                          setStopStatus(event.target.value as "COMPLETED" | "DISCONTINUED")
                        }
                      >
                        <option value="COMPLETED">Tratamiento completado</option>
                        <option value="DISCONTINUED">Discontinuado antes de término</option>
                      </select>
                    </label>
                    <label>
                      <span>Motivo</span>
                      <input
                        value={stopReason}
                        onChange={(event) => setStopReason(event.target.value)}
                        minLength={2}
                        maxLength={500}
                        placeholder={
                          stopStatus === "COMPLETED"
                            ? "Esquema indicado completado"
                            : "Motivo clínico de discontinuación"
                        }
                        autoFocus
                        required
                      />
                    </label>
                    {error && (
                      <p className="form-error" role="alert">
                        {error}
                      </p>
                    )}
                    <div className="form-actions">
                      <button
                        className="button button-secondary button-compact"
                        type="button"
                        disabled={stopping}
                        onClick={() => {
                          setStoppingId(null);
                          setError(null);
                          stopIdempotencyKeyRef.current = null;
                        }}
                      >
                        Cancelar
                      </button>
                      <button
                        className="button button-primary button-compact"
                        type="submit"
                        disabled={stopping || stopReason.trim().length < 2}
                      >
                        {stopping && <LoaderCircle className="spin" size={14} />}
                        Confirmar cierre
                      </button>
                    </div>
                  </form>
                ) : (
                  <button
                    className="button-link"
                    type="button"
                    onClick={() => {
                      setStoppingId(medication.id);
                      setError(null);
                      stopIdempotencyKeyRef.current = crypto.randomUUID();
                    }}
                  >
                    Finalizar indicación
                  </button>
                ))}
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">No hay tratamientos activos.</p>
      )}
      {historical.length > 0 && (
        <details className="medication-history">
          <summary>Ver tratamientos finalizados ({historical.length})</summary>
          <ul>
            {historical.map((medication) => (
              <li key={medication.id}>
                {medication.status === "COMPLETED" ? (
                  <CheckCircle2 size={15} aria-hidden="true" />
                ) : (
                  <Ban size={15} aria-hidden="true" />
                )}
                <span>
                  <strong>{medication.name}</strong>
                  <small>
                    {medication.status === "COMPLETED" ? "Completado" : "Discontinuado"}
                    {medication.stoppedReason ? ` · ${medication.stoppedReason}` : ""}
                  </small>
                  <details>
                    <summary>Ver indicación completa</summary>
                    <HealthFields record={medication} />
                  </details>
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
      {composerOpen ? (
        <MedicationComposer
          patientName={patientName}
          consultations={eligibleConsultations}
          onCancel={() => setComposerOpen(false)}
          onCreated={async (input, idempotencyKey) => {
            const created = await onCreated(input, idempotencyKey);
            setComposerOpen(false);
            return created;
          }}
        />
      ) : (
        canWrite && (
          <div className="medication-create-action">
            <button
              className="button button-secondary button-compact"
              type="button"
              onClick={() => setComposerOpen(true)}
              disabled={eligibleConsultations.length === 0}
            >
              <Pill size={15} /> Indicar tratamiento
            </button>
            {eligibleConsultations.length === 0 && (
              <small>Primero registrá y firmá una atención para vincular el tratamiento.</small>
            )}
            <small className="muted">Para cambiar una pauta firmada, discontinuá la anterior e indicá una nueva desde su consulta. El historial se conserva.</small>
          </div>
        )
      )}
    </section>
  );
}

function MedicationComposer({
  patientName,
  consultations,
  onCancel,
  onCreated,
}: {
  patientName: string;
  consultations: MedicationConsultation[];
  onCancel: () => void;
  onCreated: (input: MedicationInput, idempotencyKey: string) => Promise<CrmMedication>;
}): ReactNode {
  const [consultationId, setConsultationId] = useState(consultations[0]?.id ?? "");
  const [name, setName] = useState("");
  const [dosage, setDosage] = useState("");
  const [frequency, setFrequency] = useState("");
  const [frequencyUnit, setFrequencyUnit] = useState("horas");
  const [route, setRoute] = useState<MedicationRoute>("ORAL");
  const [startAt, setStartAt] = useState(todayValue);
  const [endAt, setEndAt] = useState("");
  const [instructions, setInstructions] = useState("");
  const [publicCareInstructions, setPublicCareInstructions] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idempotencyKey = useMemo(
    () => crypto.randomUUID(),
    [
      consultationId,
      name,
      dosage,
      frequency,
      frequencyUnit,
      route,
      startAt,
      endAt,
      instructions,
      publicCareInstructions,
    ],
  );

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (endAt && endAt < startAt) {
      setError("La finalización no puede ser anterior al inicio.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onCreated(
        {
          consultationId,
          name: name.trim(),
          dosage: dosage.trim(),
          frequency:
            frequencyUnit === "once"
              ? "Dosis única"
              : `Cada ${frequency} ${frequency === "1" ? frequencyUnit.slice(0, -1) : frequencyUnit}`,
          route,
          startAt: new Date(`${startAt}T00:00:00`).toISOString(),
          ...(endAt ? { endAt: new Date(`${endAt}T00:00:00`).toISOString() } : {}),
          ...(instructions.trim() ? { instructions: instructions.trim() } : {}),
          ...(publicCareInstructions.trim()
            ? { publicCareInstructions: publicCareInstructions.trim() }
            : {}),
        },
        idempotencyKey,
      );
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "No pudimos registrar la indicación.");
      setSubmitting(false);
    }
  }

  return (
    <form className="medication-composer" onSubmit={(event) => void submit(event)}>
      <div className="clinical-document-tray-heading">
        <div>
          <span className="eyebrow">Prescripción atribuida</span>
          <h3>Indicar tratamiento a {patientName}</h3>
        </div>
        <button
          className="icon-button"
          type="button"
          onClick={onCancel}
          disabled={submitting}
          aria-label="Cerrar prescripción"
        >
          <X size={16} />
        </button>
      </div>
      <label>
        <span>Atención de origen</span>
        <select
          value={consultationId}
          onChange={(event) => setConsultationId(event.target.value)}
          required
        >
          {consultations.map((consultation) => (
            <option key={consultation.id} value={consultation.id}>
              {formatClinicalDate(consultation.occurredAt)} — {consultation.reason}
            </option>
          ))}
        </select>
        <small>La indicación quedará enlazada a esta firma clínica.</small>
      </label>
      <div className="medication-field-grid">
        <label>
          <span>Medicamento o tratamiento</span>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            minLength={2}
            maxLength={160}
            autoFocus
            required
          />
        </label>
        <label>
          <span>Vía</span>
          <select
            value={route}
            onChange={(event) => setRoute(event.target.value as MedicationRoute)}
          >
            {Object.entries(ROUTE_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="medication-field-grid">
        <label>
          <span>Dosis</span>
          <input
            value={dosage}
            onChange={(event) => setDosage(event.target.value)}
            maxLength={120}
            placeholder="Ej. 250 mg"
            required
          />
        </label>
        <label>
          <span>Cada cuánto</span>
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={999}
            step={1}
            value={frequency}
            onChange={(event) => setFrequency(event.target.value)}
            placeholder="Ej. 12"
            disabled={frequencyUnit === "once"}
            required={frequencyUnit !== "once"}
          />
        </label>
        <label>
          <span>Unidad de frecuencia</span>
          <select value={frequencyUnit} onChange={(event) => setFrequencyUnit(event.target.value)}>
            <option value="horas">Horas</option>
            <option value="días">Días</option>
            <option value="semanas">Semanas</option>
            <option value="once">Dosis única</option>
          </select>
        </label>
      </div>
      <div className="medication-field-grid">
        <label>
          <span>Inicio</span>
          <input
            type="date"
            value={startAt}
            onChange={(event) => setStartAt(event.target.value)}
            required
          />
        </label>
        <label>
          <span>Finalización prevista</span>
          <input
            type="date"
            value={endAt}
            min={startAt}
            onChange={(event) => setEndAt(event.target.value)}
          />
        </label>
      </div>
      <label>
        <span>Indicaciones de administración</span>
        <textarea
          value={instructions}
          onChange={(event) => setInstructions(event.target.value)}
          maxLength={1000}
          rows={3}
          placeholder="Con alimento, conservación, observaciones…"
        />
      </label>
      <label>
        <span>Advertencias para quien encuentre a la mascota (opcional)</span>
        <textarea
          value={publicCareInstructions}
          onChange={(event) => setPublicCareInstructions(event.target.value)}
          maxLength={300}
          rows={2}
          placeholder="Sólo cuidados esenciales, sin diagnósticos ni datos personales."
        />
        <small>
          Este texto y la pauta sólo se mostrarán en el tag si el tutor principal lo autoriza. No se
          publica la historia clínica.
        </small>
      </label>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <div className="form-actions">
        <button
          className="button button-secondary button-compact"
          type="button"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancelar
        </button>
        <button
          className="button button-primary button-compact"
          type="submit"
          disabled={submitting}
        >
          {submitting && <LoaderCircle className="spin" size={15} />}
          {submitting ? "Firmando…" : "Firmar indicación"}
        </button>
      </div>
    </form>
  );
}

function todayValue(): string {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}

function formatClinicalDate(value: string): string {
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "medium" }).format(new Date(value));
}
